import { useQuery } from "@tanstack/react-query";
import type { SupabaseClient } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { isMissingRelation } from "@/stores/pm/repository";
import { briefKeys } from "../queryKeys";

/**
 * Is the brief schema deployed? The tables land in franklin-data-pipeline, so
 * there is a window where this app build is live and `project_briefs` is not.
 * In that window every brief query 404s, the realtime channel errors and
 * re-joins forever, and the nav count polls a missing table twice a minute —
 * so the tab, the page and the building-scoped hooks all wait on this probe.
 *
 * One head-only select per session (`staleTime: Infinity`, no retry): `false`
 * only when PostgREST reports the relation missing; any other failure (network,
 * a transient 5xx) reads as "present" so a blip does not hide the feature.
 */
async function probeBriefSchema(): Promise<boolean> {
  const { error } = await (supabase as unknown as SupabaseClient)
    .from("project_briefs")
    .select("id", { count: "exact", head: true })
    .limit(1);
  if (error && isMissingRelation(error)) return false;
  return true;
}

export function useBriefSchemaAvailable(enabled = true): {
  /** `undefined` while the probe is in flight. */
  available: boolean | undefined;
} {
  const query = useQuery({
    queryKey: briefKeys.schema,
    queryFn: probeBriefSchema,
    enabled,
    retry: false,
    staleTime: Infinity,
    gcTime: Infinity,
  });
  return { available: query.isError ? true : query.data };
}
